import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, debounceTime, tap } from 'rxjs';
import {
  BalanceItem,
  BasisOfPreparationValue,
  JaarrapportageEntity,
  Jaarrekening,
  JaarrekeningType,
  LegalFormValue,
  LegalSizeCriteriaValue,
  MonetaryNoDecimalsPeriodValues,
  OverigeToelichtingen,
  ProfitAppropriationValue,
  Toelichting,
  TrialBalanceItem,
  WinstEnVerliesrekening,
  WinstEnVerliesrekeningIndelingType,
  ZakelijkePartner
} from './api.bokkz.jaarstukken';
import { ErrorMessage } from './rekenclient.api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MenuInterface, SubOnderdeel, SubSubOnderdeel } from './api.bokkz.services';

@Injectable({ providedIn: 'root' })
export class BokkzJrDataService implements OnDestroy {
  public jaarrapportageEntity = new BehaviorSubject<JaarrapportageEntity>(
    this.getFromSessionStorage<JaarrapportageEntity>('jrEntity') || ({} as JaarrapportageEntity)
  );
  public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
  public options = new BehaviorSubject<MenuInterface[]>([]);
  public aantalValidaties = new BehaviorSubject<number>(0);
  public isRechtspersoon = new BehaviorSubject<boolean>(false);
  public heeftAandelen = new BehaviorSubject<boolean>(false);
  public isEenmanszaak = new BehaviorSubject<boolean>(false);
  public isCooperatie = new BehaviorSubject<boolean>(false);
  public isEnkelvoudig = new BehaviorSubject<boolean>(false);
  public isFiscaleGrondslagen = new BehaviorSubject<boolean>(false);
  public isMicro = new BehaviorSubject<boolean>(false);
  public isKlein = new BehaviorSubject<boolean>(false);
  public isMiddelgroot = new BehaviorSubject<boolean>(false);
  public isGroot = new BehaviorSubject<boolean>(false);
  public isBalansNaResultaatbestemming = new BehaviorSubject<boolean>(false);

  //balans
  public heeftImmaterieleVasteActiva = new BehaviorSubject<boolean>(false);
  public heeftMaterieleVasteActiva = new BehaviorSubject<boolean>(false);
  public heeftFinancieleVasteActiva = new BehaviorSubject<boolean>(false);
  public heeftVastgoedBeleggingen = new BehaviorSubject<boolean>(false);
  public heeftVoorraden = new BehaviorSubject<boolean>(false);
  public heeftOnderhandenProjecten = new BehaviorSubject<boolean>(false);
  public heeftVorderingen = new BehaviorSubject<boolean>(false);
  public heeftEffecten = new BehaviorSubject<boolean>(false);
  public heeftLiquideMiddelen = new BehaviorSubject<boolean>(false);
  public heeftAandelenkapitaal = new BehaviorSubject<boolean>(false);
  public heeftAgio = new BehaviorSubject<boolean>(false);
  public heeftHerwaarderingsreserve = new BehaviorSubject<boolean>(false);
  public heeftAndereWettelijkeReserves = new BehaviorSubject<boolean>(false);
  public heeftStatutaireReserves = new BehaviorSubject<boolean>(false);
  public heeftHerinvesteringsreserve = new BehaviorSubject<boolean>(false);
  public heeftOverigeReserves = new BehaviorSubject<boolean>(false);
  public heeftOverigeFiscaleReserves = new BehaviorSubject<boolean>(false);
  public heeftResultaatBoekjaar = new BehaviorSubject<boolean>(false);
  public heeftNietVerdeeldeWinsten = new BehaviorSubject<boolean>(false);
  public heeftVoorzieningen = new BehaviorSubject<boolean>(false);
  public heeftKortlopendeSchulden = new BehaviorSubject<boolean>(false);
  public heeftLanglopendeSchulden = new BehaviorSubject<boolean>(false);

  //w-en-v
  public heeftNettoOmzet = new BehaviorSubject<boolean>(false);
  public heeftWaardeveranderingenVoorraad = new BehaviorSubject<boolean>(false);
  public heeftGeactiveerdeProductieEigenBedrijf = new BehaviorSubject<boolean>(false);
  public heeftPriveGebruikEigenBedrijf = new BehaviorSubject<boolean>(false);
  public heeftExploitatiesubsidies = new BehaviorSubject<boolean>(false);
  public heeftOverigeBedrijfsopbrengsten = new BehaviorSubject<boolean>(false);
  public heeftKostenGrondEnHulpstoffen = new BehaviorSubject<boolean>(false);
  public heeftKostenUitbesteedWerk = new BehaviorSubject<boolean>(false);
  public heeftLastenUitHoofdePersoneelsbeloningen = new BehaviorSubject<boolean>(false);
  public heeftAfschrijvingenIvaMva = new BehaviorSubject<boolean>(false);
  public heeftWaardeveranderingenIvaMva = new BehaviorSubject<boolean>(false);
  public heeftWaardeveranderingenVastgoedbeleggingen = new BehaviorSubject<boolean>(false);
  public heeftWaardeverminderingenVlottendeActiva = new BehaviorSubject<boolean>(false);
  public heeftOverigeBedrijfskosten = new BehaviorSubject<boolean>(false);
  public heeftBedrijfslasten = new BehaviorSubject<boolean>(false);
  public heeftVrijvalUitHerwaarderingsreserve = new BehaviorSubject<boolean>(false);
  public heeftFinancieleBatenLasten = new BehaviorSubject<boolean>(false);
  public heeftBelastingen = new BehaviorSubject<boolean>(false);
  public heeftResultaatUitDeelnemingen = new BehaviorSubject<boolean>(false);

  constructor(private fb: FormBuilder) {
    this.applyDebounceTime();
  }

  private applyDebounceTime() {
      this.jaarrapportageEntity.pipe(
          debounceTime(500),
          tap((value) => this.saveToSessionStorage('jrEntity', value))).subscribe();
  }

  isLegalSizeMediumOrLarger(): boolean {
      return this.isMiddelgroot.getValue() || this.isGroot.getValue();
  }

  // Voor het ophalen uit de sessionstorage.
  private getFromSessionStorage<T>(key: string): T | null {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }

  // Voor het opslaan in sessionstorage
  private saveToSessionStorage(key: string, value: any) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  public updateEntity(jaarrekeningEnity: JaarrapportageEntity) {
    const informatieEntiteit = jaarrekeningEnity.rapportage?.informatieEntiteit;
    if (informatieEntiteit) {
      const adres = informatieEntiteit.adres;
      if (adres && adres.postalCodeNL && adres.postalCodeNL.value) {
        adres.postalCodeNL.value = this.formatPostcode(adres.postalCodeNL.value);
      }

      const legalForm = informatieEntiteit.legalEntityLegalForm?.value;

      this.isEenmanszaak.next(legalForm === LegalFormValue.EENMANSZAAK);
      this.isCooperatie.next(legalForm === LegalFormValue.COOPERATIE);

      switch (legalForm) {
        case LegalFormValue.EENMANSZAAK:
        case LegalFormValue.VOF:
        case LegalFormValue.MAATSCHAP:
        case LegalFormValue.CV:
        case LegalFormValue.REDERIJ:
        case LegalFormValue.OV:
        case LegalFormValue.OVERIG:
          this.isRechtspersoon.next(false);
          this.heeftAandelen.next(false);
          break;

        case LegalFormValue.BV:
        case LegalFormValue.NV:
        case LegalFormValue.COOPERATIE:
          this.isRechtspersoon.next(true);
          this.heeftAandelen.next(true);
          break;

        case LegalFormValue.STICHTING:
        case LegalFormValue.VERENIGING:
          this.isRechtspersoon.next(true);
          this.heeftAandelen.next(false);
          break;

        default:
          break;
      }

      const legalSize = informatieEntiteit.legalSizeCriteriaClassification?.value;

      this.isMicro.next(false);
      this.isKlein.next(false);
      this.isMiddelgroot.next(false);
      this.isGroot.next(false);

      switch (legalSize) {
        case LegalSizeCriteriaValue.MICRO:
          this.isMicro.next(true);
          break;
        case LegalSizeCriteriaValue.KLEIN:
          this.isKlein.next(true);
          break;
        case LegalSizeCriteriaValue.MIDDELGROOT:
          this.isMiddelgroot.next(true);
          break;
        case LegalSizeCriteriaValue.GROOT:
          this.isGroot.next(true);
          break;
        default:
          break;
      }
    }

    this.isFiscaleGrondslagen.next(false);
    if (jaarrekeningEnity.rapportage?.informatieRapportage?.basisOfPreparation?.value === BasisOfPreparationValue.FISCAAL) {
      this.isFiscaleGrondslagen.next(true);
    }

    this.isEnkelvoudig.next(true);
    this.isBalansNaResultaatbestemming.next(false);

    //balans
    this.heeftImmaterieleVasteActiva.next(false);
    this.heeftMaterieleVasteActiva.next(false);
    this.heeftFinancieleVasteActiva.next(false);
    this.heeftVastgoedBeleggingen.next(false);
    this.heeftVoorraden.next(false);
    this.heeftOnderhandenProjecten.next(false);
    this.heeftVorderingen.next(false);
    this.heeftEffecten.next(false);
    this.heeftLiquideMiddelen.next(false);
    this.heeftAandelenkapitaal.next(false);
    this.heeftAgio.next(false);
    this.heeftHerwaarderingsreserve.next(false);
    this.heeftAndereWettelijkeReserves.next(false);
    this.heeftStatutaireReserves.next(false);
    this.heeftHerinvesteringsreserve.next(false);
    this.heeftOverigeReserves.next(false);
    this.heeftOverigeFiscaleReserves.next(false);
    this.heeftResultaatBoekjaar.next(false);
    this.heeftNietVerdeeldeWinsten.next(false);
    this.heeftVoorzieningen.next(false);
    this.heeftLanglopendeSchulden.next(false);
    this.heeftKortlopendeSchulden.next(false);

    //w-en-v
    this.heeftNettoOmzet.next(false);
    this.heeftWaardeveranderingenVoorraad.next(false);
    this.heeftGeactiveerdeProductieEigenBedrijf.next(false);
    this.heeftPriveGebruikEigenBedrijf.next(false);
    this.heeftExploitatiesubsidies.next(false);
    this.heeftOverigeBedrijfsopbrengsten.next(false);
    this.heeftKostenGrondEnHulpstoffen.next(false);
    this.heeftKostenUitbesteedWerk.next(false);
    this.heeftLastenUitHoofdePersoneelsbeloningen.next(false);
    this.heeftAfschrijvingenIvaMva.next(false);
    this.heeftWaardeveranderingenIvaMva.next(false);
    this.heeftWaardeveranderingenVastgoedbeleggingen.next(false);
    this.heeftWaardeverminderingenVlottendeActiva.next(false);
    this.heeftOverigeBedrijfskosten.next(false);
    this.heeftBedrijfslasten.next(false);
    this.heeftVrijvalUitHerwaarderingsreserve.next(false);
    this.heeftFinancieleBatenLasten.next(false);
    this.heeftBelastingen.next(false);
    this.heeftResultaatUitDeelnemingen.next(false);

    if (jaarrekeningEnity.rapportage?.jaarrekeningen?.at(0)) {
      const jaarrekening = jaarrekeningEnity.rapportage?.jaarrekeningen[0];

      if (jaarrekening.jaarrekeningType === undefined) {
        jaarrekening.jaarrekeningType = JaarrekeningType.ENKELVOUDIG;
      }
      if (jaarrekening.jaarrekeningType === JaarrekeningType.GECONSOLIDEERD) {
        this.isEnkelvoudig.next(false);
      }
      if (jaarrekening.winstEnVerliesrekening === undefined) {
        jaarrekening.winstEnVerliesrekening = {} as WinstEnVerliesrekening;
        jaarrekening.winstEnVerliesrekening.winstEnVerliesrekeningIndelingType =
          WinstEnVerliesrekeningIndelingType.BEDRIJFSOPBRENGSTEN_BEDRIJFSLASTEN;
      }
      if (jaarrekening.toelichting === undefined) {
        jaarrekening.toelichting = {} as Toelichting;
        jaarrekening.toelichting.overigeToelichtingen = {} as OverigeToelichtingen;
      }

      const balans = jaarrekening.balans;
      if (balans) {
        if (balans.balanceSheetBeforeAfterAppropriationResults?.value === undefined) {
          balans.balanceSheetBeforeAfterAppropriationResults.value = {} as ProfitAppropriationValue;
          balans.balanceSheetBeforeAfterAppropriationResults.value = ProfitAppropriationValue.VOOR;
        }
        if (balans.balanceSheetBeforeAfterAppropriationResults?.value === ProfitAppropriationValue.NA) {
          this.isBalansNaResultaatbestemming.next(true);
        }
        if (this.hasPeriodValues(balans.immaterieleVasteActiva?.intangibleAssets)) {
          this.heeftImmaterieleVasteActiva.next(true);
        }
        if (this.hasPeriodValues(balans.materieleVasteActiva?.propertyPlantEquipment)) {
          this.heeftMaterieleVasteActiva.next(true);
        }
        if (this.hasPeriodValues(balans.financieleVasteActiva?.financialAssets)) {
          this.heeftFinancieleVasteActiva.next(true);
        }
        if (this.hasPeriodValues(balans.vastgoedbeleggingen?.investmentProperties)) {
          this.heeftVastgoedBeleggingen.next(true);
        }
        if (this.hasPeriodValues(balans.voorraden?.inventories)) {
          this.heeftVoorraden.next(true);
        }
        if (this.hasPeriodValues(balans.onderhandenProjecten?.constructionContractsAssets)) {
          this.heeftOnderhandenProjecten.next(true);
        }
        if (this.hasPeriodValues(balans.vorderingen?.receivables)) {
          this.heeftVorderingen.next(true);
        }
        if (this.hasPeriodValues(balans.effecten?.securitiesCurrent)) {
          this.heeftEffecten.next(true);
        }
        if (this.hasPeriodValues(balans.liquideMiddelen?.cashAndCashEquivalents)) {
          this.heeftLiquideMiddelen.next(true);
        }
        if (this.hasPeriodValues(balans.aandelenkapitaal?.shareCapitalPaidCalledUp)) {
          this.heeftAandelenkapitaal.next(true);
        }
        if (this.hasPeriodValues(balans.agio?.sharePremium)) {
          this.heeftAgio.next(true);
        }
        if (this.hasPeriodValues(balans.herwaarderingsreserve?.revaluationReserve)) {
          this.heeftHerwaarderingsreserve.next(true);
        }
        if (this.hasPeriodValues(balans.andereWettelijkeReserves?.legalReserves)) {
          this.heeftAndereWettelijkeReserves.next(true);
        }
        if (this.hasPeriodValues(balans.statutaireReserves?.statutoryReserves)) {
          this.heeftStatutaireReserves.next(true);
        }
        if (this.hasPeriodValues(balans.herinvesteringsreserve?.reinvestmentReserve)) {
          this.heeftHerinvesteringsreserve.next(true);
        }
        if (this.hasPeriodValues(balans.overigeReserves?.reservesOther)) {
          this.heeftOverigeReserves.next(true);
        }
        if (this.hasPeriodValues(balans.overigeFiscaleReserves?.otherFiscalReserves)) {
          this.heeftOverigeFiscaleReserves.next(true);
        }
        if (this.hasPeriodValues(balans.resultaatBoekjaar?.resultForTheYear)) {
          this.heeftResultaatBoekjaar.next(true);
        }
        if (this.hasPeriodValues(balans.nietVerdeeldeWinsten?.retainedEarnings)) {
          this.heeftNietVerdeeldeWinsten.next(true);
        }
        if (this.hasPeriodValues(balans.voorzieningen?.provisions)) {
          this.heeftVoorzieningen.next(true);
        }
        if (this.hasPeriodValues(balans.langlopendeSchulden?.liabilitiesNoncurrent)) {
          this.heeftLanglopendeSchulden.next(true);
        }
        if (this.hasPeriodValues(balans.kortlopendeSchulden?.liabilitiesCurrent)) {
          this.heeftKortlopendeSchulden.next(true);
        }
      }

      const winstEnVerliesrekening = jaarrekening.winstEnVerliesrekening;
      if (winstEnVerliesrekening) {
        if (this.hasPeriodValues(winstEnVerliesrekening.nettoOmzet?.netRevenue)) {
          this.heeftNettoOmzet.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.waardeveranderingenVoorraad?.changesInventoriesWorkInProgress)) {
          this.heeftWaardeveranderingenVoorraad.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.geactiveerdeProductieEigenBedrijf?.capitalisedProductionCosts)) {
          this.heeftGeactiveerdeProductieEigenBedrijf.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.priveGebruikEigenBedrijf?.privateUseOwnBusiness)) {
          this.heeftPriveGebruikEigenBedrijf.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.exploitatiesubsidies?.exploitationSubsidies)) {
          this.heeftExploitatiesubsidies.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.overigeBedrijfsopbrengsten?.operatingIncomeOther)) {
          this.heeftOverigeBedrijfsopbrengsten.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.kostenGrondEnHulpstoffen?.costsRawMaterialsConsumables)) {
          this.heeftKostenGrondEnHulpstoffen.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.kostenUitbesteedWerk?.costsOutsourcedWorkOtherExternalExpenses)) {
          this.heeftKostenUitbesteedWerk.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.lastenUitHoofdePersoneelsbeloningen?.employeeBenefitsExpenses)) {
          this.heeftLastenUitHoofdePersoneelsbeloningen.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.afschrijvingenIvaMva?.depreciationPropertyPlantEquipmentAmortisationIntangibleAssets)) {
          this.heeftAfschrijvingenIvaMva.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.overigeWaardeveranderingenIvaMva?.changesInValueIntangibleAssetsPropertyPlantEquipment)) {
          this.heeftWaardeveranderingenIvaMva.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.waardeveranderingenVastgoedbeleggingen?.changesValueInvestmentProperties)) {
          this.heeftWaardeveranderingenVastgoedbeleggingen.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.waardeverminderingVlottendeActiva?.impairmentCurrentAssets)) {
          this.heeftWaardeverminderingenVlottendeActiva.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.overigeBedrijfskosten?.operatingExpensesOther)) {
          this.heeftOverigeBedrijfskosten.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.bedrijfslasten?.operatingExpenses)) {
          this.heeftBedrijfslasten.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.uitHerwaarderingsreserveVrijgevallenBedragen?.revaluationReserveRelease)) {
          this.heeftVrijvalUitHerwaarderingsreserve.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.financieleBatenLasten?.financialIncomeExpenses)) {
          this.heeftFinancieleBatenLasten.next(true);
        }
        if (this.hasPeriodValues(winstEnVerliesrekening.belastingen?.incomeTaxExpense)) {
          this.heeftBelastingen.next(true);
        }
        if (
          this.hasPeriodValues(
            winstEnVerliesrekening.aandeelResultaatOndernemingenWaarinWordtDeelgenomen?.shareInResultsParticipatingInterests
          )
        ) {
          this.heeftResultaatUitDeelnemingen.next(true);
        }
      }
    }
    this.updateMenuInterface(jaarrekeningEnity);
    this.jaarrapportageEntity.next(jaarrekeningEnity);
  }

  updateErrorResponse(errorResponse: ErrorMessage[]) {
    this.errorResponse.next(errorResponse);
  }

  updateAantalValidaties(aantalValidaties: number) {
    this.aantalValidaties.next(aantalValidaties);
  }

  public createFormGroupMonetaryNoDecimalsPeriodValues() {
    return this.fb.group({
      valuePeriod1: null as unknown as number,
      valuePeriod2: null as unknown as number
    });
  }

  public createFormGroupBalanceItem() {
    return this.fb.group({
      valuePeriod1: null as unknown as number,
      valuePeriod2: null as unknown as number,
      trialBalanceItems: this.fb.array<TrialBalanceItem>([])
    });
  }

  public createFormGroupCustomDate() {
    return this.fb.group({
      value: null as unknown as Date
    })
  }

  public createProfitAppropriationItem() {
    return this.fb.group({
      value: [null, Validators.required]
    });
  }

  public createFormGroupString() {
    return this.fb.group({
      value: null as unknown as string
    })
  }

  public createFormGroupTrialBalanceItem(item?: TrialBalanceItem): FormGroup {
    const periodValue = item?.periodValues as MonetaryNoDecimalsPeriodValues;
    return this.fb.group({
      rgsCode: item ? item.rgsCode : (null as unknown as string),
      account: item ? item.account : (null as unknown as string),
      accountDescription: item ? item.accountDescription : (null as unknown as string),
      periodValues: this.fb.group({
        valuePeriod1: periodValue ? periodValue.valuePeriod1 : (null as unknown as number),
        valuePeriod2: periodValue ? periodValue.valuePeriod2 : (null as unknown as number)
      }),
      autogenerated: item ? item.autogenerated : undefined,
    });
  }

  public createFormGroupFormattedExplanation() {
    return this.fb.group({
      includeInKvkReport: false,
      value: null as unknown as string
    });
  }

  public createFormGroupFormattedExplanationPolicy() {
    return this.fb.group({
      value: null as unknown as string,
      includeInKvkReport: false,
      selected: null as unknown as 'null',
      customPolicy: false
    });
  }

  public createFormGroupZakelijkePartner(zakelijkePartner?: ZakelijkePartner): FormGroup {
    return this.fb.group({
      identificationNumberTypedMember: this.fb.group({
        value: zakelijkePartner?.identificationNumberTypedMember?.value || null as unknown as string
      }),
      prefix:  this.fb.group({
        value: zakelijkePartner?.prefix?.value || null as unknown as string
      }),
      familyName: this.fb.group({
        value: zakelijkePartner?.familyName?.value || null as unknown as string
      }),
      firstName: this.fb.group({
        value: zakelijkePartner?.firstName?.value || null as unknown as string
      }),
      dateOfBirth: this.fb.group({
        value: zakelijkePartner?.dateOfBirth?.value || null as unknown as Date
      }),
      ondertekeningJaarrekening: this.fb.group({
        representativeType: this.fb.group({
          value: zakelijkePartner ? zakelijkePartner.ondertekeningJaarrekening?.representativeType?.value : null as unknown as string
        }),
        nameRepresentative: this.fb.group({
          value: zakelijkePartner ? zakelijkePartner.ondertekeningJaarrekening?.nameRepresentative?.value : null as unknown as string
        }),
        signatureRepresentativeLocation: this.fb.group({
          value: zakelijkePartner ? zakelijkePartner.ondertekeningJaarrekening?.signatureRepresentativeLocation?.value : null as unknown as string
        }),
        signatureRepresentativeDate: this.fb.group({
          value: zakelijkePartner ? zakelijkePartner.ondertekeningJaarrekening?.signatureRepresentativeDate?.value : null as unknown as Date
        }),
        representativeSignedStatus: this.fb.group({
          value: true
        }),
        representativeReasonNoSignature: this.fb.group({
          value: zakelijkePartner ? zakelijkePartner.ondertekeningJaarrekening?.representativeReasonNoSignature?.value : null as unknown as string
        })
      })
    })
  }

  formatPostcode(postcode: string | null | undefined): string {
    if (!postcode) return ''; // Retourneer een lege string als postcode null of undefined is

    // Verwijder alle niet-alfanumerieke tekens (bijv. spaties)
    const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');

    // Zorg voor 4 cijfers en 2 hoofdletters
    if (cleanedPostcode.length >= 4) {
      const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, ''); // Alleen cijfers
      const letters = cleanedPostcode
        .substring(4, 6)
        .replace(/[^a-zA-Z]/g, '')
        .toUpperCase(); // Alleen letters
      return `${cijfers}${letters}`;
    }

    return cleanedPostcode; // Als de postcode te kort is, retourneer de schoongemaakte versie
  }

  ngOnDestroy(): void {
    if (this.jaarrapportageEntity) {
      this.jaarrapportageEntity.unsubscribe();
    }
    if (this.isRechtspersoon) {
      this.isRechtspersoon.unsubscribe();
    }
    if (this.heeftAandelen) {
      this.heeftAandelen.unsubscribe();
    }
    if (this.isEenmanszaak) {
      this.isEenmanszaak.unsubscribe();
    }
    if (this.isCooperatie) {
      this.isCooperatie.unsubscribe();
    }
    if (this.isEnkelvoudig) {
      this.isEnkelvoudig.unsubscribe();
    }
    if (this.isFiscaleGrondslagen) {
      this.isFiscaleGrondslagen.unsubscribe();
    }
    if (this.isMicro) {
      this.isMicro.unsubscribe();
    }
    if (this.isKlein) {
      this.isKlein.unsubscribe();
    }
    if (this.isMiddelgroot) {
      this.isMiddelgroot.unsubscribe();
    }
    if (this.isGroot) {
      this.isGroot.unsubscribe();
    }

    //balans
    [
      this.heeftImmaterieleVasteActiva,
      this.heeftMaterieleVasteActiva,
      this.heeftFinancieleVasteActiva,
      this.heeftVastgoedBeleggingen,
      this.heeftVoorraden,
      this.heeftOnderhandenProjecten,
      this.heeftVorderingen,
      this.heeftEffecten,
      this.heeftLiquideMiddelen,
      this.heeftAandelenkapitaal,
      this.heeftAgio,
      this.heeftHerwaarderingsreserve,
      this.heeftAndereWettelijkeReserves,
      this.heeftStatutaireReserves,
      this.heeftHerinvesteringsreserve,
      this.heeftOverigeReserves,
      this.heeftOverigeFiscaleReserves,
      this.heeftResultaatBoekjaar,
      this.heeftNietVerdeeldeWinsten,
      this.heeftVoorzieningen,
      this.heeftKortlopendeSchulden,
      this.heeftLanglopendeSchulden
    ].forEach((b) => b?.unsubscribe());

    //w-en-v
    [
      this.heeftNettoOmzet,
      this.heeftWaardeveranderingenVoorraad,
      this.heeftGeactiveerdeProductieEigenBedrijf,
      this.heeftPriveGebruikEigenBedrijf,
      this.heeftExploitatiesubsidies,
      this.heeftOverigeBedrijfsopbrengsten,
      this.heeftKostenGrondEnHulpstoffen,
      this.heeftKostenUitbesteedWerk,
      this.heeftLastenUitHoofdePersoneelsbeloningen,
      this.heeftAfschrijvingenIvaMva,
      this.heeftWaardeveranderingenIvaMva,
      this.heeftWaardeveranderingenVastgoedbeleggingen,
      this.heeftWaardeverminderingenVlottendeActiva,
      this.heeftOverigeBedrijfskosten,
      this.heeftBedrijfslasten,
      this.heeftVrijvalUitHerwaarderingsreserve,
      this.heeftFinancieleBatenLasten,
      this.heeftBelastingen,
      this.heeftResultaatUitDeelnemingen
    ].forEach((beh) => beh?.unsubscribe());

    if (this.errorResponse) {
      this.errorResponse.unsubscribe();
    }
  }

  private hasPeriodValues(obj?: MonetaryNoDecimalsPeriodValues | BalanceItem): boolean {
    return obj?.valuePeriod1 != null || obj?.valuePeriod2 != null;
  }

  public getDisplayNameZakelijkPartner(item: ZakelijkePartner, indexZakelijkePartner: number): string {
    if (!item) {
      return `Zakelijke partner ${indexZakelijkePartner + 1}`;
    }

    const familyName = item.familyName?.value;
    const firstName = item.firstName?.value ?? '';
    const id = item.identificationNumberTypedMember?.value ?? (indexZakelijkePartner + 1);
    const prefix = item.prefix?.value ?? '';

    if (!familyName) {
      return `Zakelijke partner ${indexZakelijkePartner + 1}`;
    }

    const nameWithPrefix = prefix
      ? `${prefix.charAt(0).toUpperCase() + prefix.slice(1)} ${familyName}`
      : familyName;

    return `${nameWithPrefix}${firstName ? `, ${firstName}` : ''}${id ? ` (${id})` : ''}`;
  }

  updateMenuInterface(jaarrekeningEntity : JaarrapportageEntity) {

    const isRechtspersoon = this.isRechtspersoon.getValue()
    const heeftAandelen = this.heeftAandelen.getValue()
    const isCooperatie = this.isCooperatie.getValue()
    const isEenmanszaak = this.isEenmanszaak.getValue()

    let subsubonderdelen: SubSubOnderdeel[] | undefined;

    const zakelijkePartners = jaarrekeningEntity?.rapportage?.zakelijkePartners;
    if (zakelijkePartners) {
      subsubonderdelen = zakelijkePartners.map((item, idx) => ({
        label: this.getDisplayNameZakelijkPartner(item, idx),
        pageMapping: `Eigen vermogen zakelijke partner (de eigenaar, vennoot of maat)/${idx}`,
        visible: true,
        value: 11 + idx
      }));
    }

    const eigenVermogenNatuurlijkPersoon: SubOnderdeel = {
      label: 'Eigen vermogen',
      pageMapping: 'Eigen vermogen onderneming',
      visible: !isRechtspersoon,
      value: 11,
      subsubonderdelen
    };

    const menuInterface: MenuInterface[] = [
      {
        value: 0,
        id: 'Algemeen',
        label: 'Algemeen',
        visible: true,
        subonderdelen: [
          {
            label: 'Informatie entiteit',
            pageMapping: 'Informatie entiteit',
            visible: true,
            value: 0
          },
          {
            label: 'Informatie rapportage',
            pageMapping: 'Informatie rapportage',
            visible: true,
            value: 1
          },
          {
            label: 'Zakelijke partners (de eigenaar, vennoten of maten)',
            pageMapping: 'Zakelijke partners (de eigenaar, vennoten of maten)',
            visible: !isRechtspersoon,
            value: 2
          },
          {
            label: 'Administratie inlezen',
            pageMapping: 'Administratie koppelen',
            visible: true,
            value: 3
          },
        ],
      },
      // {
      //   value: 1,
      //   id: 'Bestuursverslag',
      //   label: 'Bestuursverslag',
      //   visible: true,
      //   subonderdelen: [
      //     {
      //       label: 'Algemene informatie',
      //       pageMapping: 'Algemene informatie',
      //       visible: true,
      //       value: 0
      //     },
      //     {
      //       label: 'Financiele informatie',
      //       pageMapping: 'Financiele informatie',
      //       visible: true,
      //       value: 1
      //     },
      //   ],
      // },
      {
        value: 2,
        id: 'Balans en winst-en-verliesrekening',
        label: 'Balans en winst-en-verliesrekening',
        visible: true,
        subonderdelen: [
          {
            label: 'Activa',
            pageMapping: 'Activa',
            visible: true,
            value: 0
          },
          {
            label: 'Passiva',
            pageMapping: 'Passiva',
            visible: true,
            value: 1
          },
          {
            label: 'Winst-en-verliesrekening',
            pageMapping: 'Winst-en-verliesrekening',
            visible: true,
            value: 2
          },
        ],
      },
      {
        value: 3,
        id: 'Algemene toelichting',
        label: 'Algemene toelichting',
        visible: true,
        subonderdelen: [
          {
            label: 'Algemeen',
            pageMapping: 'Algemene toelichting',
            visible: true,
            value: 0
          },
          {
            label: 'Continuïteit',
            pageMapping: 'Continuïteit',
            visible: true,
            value: 1,
          },
          {
            label: 'Schattingen',
            pageMapping: 'Schattingen',
            visible: true,
            value: 2,
          },
          {
            label: 'Consolidatie',
            pageMapping: 'Consolidatie',
            visible: isRechtspersoon && heeftAandelen,
            value: 3,
          },
          {
            label: 'Fusies en overnames',
            pageMapping: 'Fusies en overnames',
            visible: true,
            value: 4,
          },
          {
            label: 'Bedrijfsactiviteiten die niet duurzaam worden voortgezet',
            pageMapping: 'Bedrijfsactiviteiten die niet duurzaam worden voortgezet',
            visible: true,
            value: 5,
          }
        ],
      },
      {
        value: 4,
        id: 'grondslagen',
        label: 'Grondslagen',
        visible: true,
        subonderdelen: [
          {
            label: 'Algemene grondslagen',
            pageMapping: 'Algemene grondslagen',
            visible: true,
            value: 0
          },
          {
            label: 'Grondslagen voor de waardering van activa en passiva',
            pageMapping: 'Grondslagen voor de waardering van activa en passiva',
            visible: true,
            value: 1
          },
          {
            label: 'Grondslagen voor de bepaling van het resultaat',
            pageMapping: 'Grondslagen voor de bepaling van het resultaat',
            visible: true,
            value: 2
          },
        ]
      },
      {
        value: 5,
        id: 'Toelichting balans',
        label: 'Toelichting balans',
        visible: true,
        subonderdelen: [
          {
            label: 'Immateriële vaste activa',
            pageMapping: 'Immateriële vaste activa',
            visible: true,
            value: 1
          },
          {
            label: 'Materiële vaste activa',
            pageMapping: 'Materiële vaste activa',
            visible: true,
            value: 2
          },
          {
            label: 'Vastgoedbeleggingen',
            pageMapping: 'Vastgoedbeleggingen',
            visible: true,
            value: 3
          },
          {
            label: 'Financiële vaste activa',
            pageMapping: 'Financiële vaste activa',
            visible: true,
            value: 4
          },
          {
            label: 'Voorraden',
            pageMapping: 'Voorraden',
            visible: true,
            value: 5
          },
          {
            label: 'Onderhanden projecten',
            pageMapping: 'Onderhanden projecten',
            visible: true,
            value: 6
          },
          {
            label: 'Vorderingen',
            pageMapping: 'Vorderingen',
            visible: true,
            value: 7
          },
          {
            label: 'Effecten',
            pageMapping: 'Effecten',
            visible: true,
            value: 8
          },
          {
            label: 'Liquide middelen',
            pageMapping: 'Liquide middelen',
            visible: true,
            value: 9
          },
          eigenVermogenNatuurlijkPersoon,
          {
            label: 'Eigen vermogen',
            pageMapping: 'Eigen vermogen',
            visible: isRechtspersoon,
            value: 12
          },
          {
            label: 'Voorzieningen',
            pageMapping: 'Voorzieningen',
            visible: true,
            value: 13
          },
          {
            label: 'Langlopende schulden',
            pageMapping: 'Langlopende schulden',
            visible: true,
            value: 14
          },
          {
            label: 'Kortlopende schulden',
            pageMapping: 'Kortlopende schulden',
            visible: true,
            value: 15
          },
          {
            label: 'Niet in de balans opgenomen activa en verplichtingen',
            pageMapping: 'Niet in balans opgenomen activa en verplichtingen',
            visible: true,
            value: 16
          },
          {
            label: 'Overige toelichtingen',
            pageMapping: 'Overige toelichtingen balans',
            visible: isRechtspersoon && heeftAandelen,
            value: 17
          }
        ]
      },
      {
        value: 6,
        id: 'Toelichting winst-en-verliesrekening',
        label: 'Toelichting winst-en-verliesrekening',
        visible: true,
        subonderdelen: [
          // {
          //   label: 'Netto-omzet',
          //   pageMapping: 'Netto-omzet',
          //   visible: true,
          //   value: 1
          // },
          {
            label: 'Bedrijfsopbrengsten',
            pageMapping: 'Bedrijfsopbrengsten',
            visible: true,
            value: 2
          },
          {
            label: 'Kosten van grond- en hulpstoffen',
            pageMapping: 'Kosten van grond- en hulpstoffen',
            visible: true,
            value: 3
          },
          {
            label: 'Kosten uitbesteed werk en andere externe kosten',
            pageMapping: 'Kosten uitbesteed werk en andere externe kosten',
            visible: true,
            value: 4
          },
          {
            label: 'Lasten uit hoofde van personeelsbeloningen',
            pageMapping: 'Lasten uit hoofde van personeelsbeloningen',
            visible: true,
            value: 5
          },
          {
            label: 'Afschrijvingen op immateriële en materiële vaste activa',
            pageMapping: 'Afschrijvingen op immateriële en materiële vaste activa',
            visible: true,
            value: 6
          },
          {
            label: 'Waardeveranderingen van immateriële en materiële vaste activa',
            pageMapping: 'Waardeveranderingen van immateriële en materiële vaste activa',
            visible: true,
            value: 7
          },
          {
            label: 'Wijziging in de reële waarde van vastgoedbeleggingen',
            pageMapping: 'Wijziging in de reële waarde van vastgoedbeleggingen',
            visible: true,
            value: 8
          },
          {
            label: 'Waardeverminderingen van vlottende activa',
            pageMapping: 'Waardeverminderingen van vlottende activa',
            visible: true,
            value: 10
          },
          {
            label: 'Overige bedrijfskosten',
            pageMapping: 'Overige bedrijfskosten',
            visible: true,
            value: 11
          },
          {
            label: 'Vrijval uit herwaarderingsreserve',
            pageMapping: 'Vrijval uit herwaarderingsreserve',
            visible: isRechtspersoon,
            value: 12
          },
          {
            label: 'Financiële baten en lasten',
            pageMapping: 'Financiële baten en lasten',
            visible: true,
            value: 13
          },
          {
            label: 'Belastingen',
            pageMapping: 'Belastingen',
            visible: isRechtspersoon,
            value: 14
          },
          // {
          //   label: 'Overige baten en lasten',
          //   pageMapping: 'Overige baten en lasten',
          //   visible: isRechtspersoon,
          //   value: 15
          // },
          {
            label: 'Resultaat uit deelnemingen',
            pageMapping: 'Resultaat uit deelnemingen',
            visible: true,
            value: 16
          },
          {
            label: 'Bijzondere posten',
            pageMapping: 'Bijzondere posten',
            visible: true,
            value: 17
          },
          {
            label: 'Overige toelichtingen',
            pageMapping: 'Overige toelichtingen winst-en-verliesrekening',
            visible: true,
            value: 18
          }
        ]
      },
      {
        value: 7,
        id: 'overige toelichtingen',
        label: 'Overige toelichtingen',
        visible: true,
        subonderdelen: [
          {
            label: 'Concessies',
            pageMapping: 'Concessies',
            visible: true,
            value: 0
          },
          {
            label: 'Gemiddeld aantal werknemers',
            pageMapping: 'Gemiddeld aantal werknemers',
            visible: true,
            value: 1
          },
          {
            label: 'Financiële instrumenten',
            pageMapping: 'Financiële instrumenten',
            visible: true,
            value: 2
          },
          {
            label: 'Op aandelen gebaseerde betalingen',
            pageMapping: 'Op aandelen gebaseerde betalingen',
            visible: isRechtspersoon && heeftAandelen && !isCooperatie,
            value: 3
          },
          {
            label: 'Bezoldiging bestuurders en commissarissen',
            pageMapping: 'Bezoldiging bestuurders en commissarissen',
            visible: isRechtspersoon && heeftAandelen,
            value: 4
          },
          {
            label: 'Leningen, voorschotten en garanties bestuurders en commissarissen',
            pageMapping: 'Leningen, voorschotten en garanties bestuurders en commissarissen',
            visible: isRechtspersoon && heeftAandelen,
            value: 5
          },
          {
            label: 'Nog niet uitgeoefende verleende rechten',
            pageMapping: 'Nog niet uitgeoefende verleende rechten',
            visible: isRechtspersoon && heeftAandelen,
            value: 6
          },
          {
            label: 'Gebeurtenissen na balansdatum',
            pageMapping: 'Gebeurtenissen na balansdatum',
            visible: true,
            value: 7
          },
          {
            label: 'Overige toelichtingen',
            pageMapping: 'Overige toelichtingen',
            visible: true,
            value: 8
          }
        ]
      },
      {
        value: 8,
        id: 'ondertekening van de jaarrekening',
        label: 'Ondertekening van de jaarrekening',
        visible: true,
        subonderdelen: [
          {
            label: 'Bestuurders, commissarissen en vertegenwoordigers',
            pageMapping: 'Ondertekening door bestuurders, commissarissen en vertegenwoordigers',
            visible: isRechtspersoon,
            value: 0
          },
          {
            label: isEenmanszaak ? 'Verantwoordelijke of gevolmachtigde' : 'Verantwoordelijken en gevolmachtigden',
            pageMapping: 'Ondertekening door verantwoordelijken en gevolmachtigden',
            visible: !isRechtspersoon,
            value: 1
          }
        ],
      },
      {
        value: 9,
        id: 'Accountantsverklaring',
        label: 'Accountantsverklaring',
        visible: true,
        subonderdelen: [
          {
            label: 'Verklaring genereren',
            pageMapping: 'Verklaring genereren',
            visible: true,
            value: 0
          },
          {
            label: 'Samenstellingsverklaring',
            pageMapping: 'Samenstellingsverklaring',
            visible: true,
            value: 1
          }
        ]
      }
    ];
    this.options.next(menuInterface);
  }
}
